import React, {
  useRef,
  useState,
  useCallback,
  useMemo,
  ReactElement,
  KeyboardEvent,
  ChangeEvent,
  MouseEvent,
  ReactNode,
  useEffect,
} from 'react';
import styled from 'styled-components/macro';
import Tippy, { TippyProps } from '@tippyjs/react/headless';
import { Placement } from 'tippy.js';
import partial from 'lodash-es/partial';
import { LazyTippy } from '../LazyTippy';
import { GreenCheckIcon } from 'assets/icons';
import Tooltip from '../Tooltip';
import LoadingIndicator from '../LoadingIndicator';
import DropdownOption from './DropdownOption';
import { Button } from '@televet/kibble-ui';

interface IDimensions {
  width: number;
  height: number;
}

export enum PopoverPlacement {
  Top = 'top',
  TopStart = 'top-start',
  TopEnd = 'top-end',
  Right = 'right',
  RightStart = 'right-start',
  RightEnd = 'right-end',
  Bottom = 'bottom',
  BottomStart = 'bottom-start',
  BottomEnd = 'bottom-end',
  Left = 'left',
  LeftStart = 'left-start',
  LeftEnd = 'left-end',
  Auto = 'auto',
  AutoStart = 'auto-start',
  AutoEnd = 'auto-end',
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface IOption<T = any> {
  text: string;
  value: T;
  isSelected?: boolean;
  icon?: ReactElement;
  isDisabled?: boolean;
  isLabel?: boolean;
  isBold?: boolean;
  closeOnClick?: boolean;
  disabledMessage?: string | ReactNode;
}

interface IDropdownProps extends TippyProps {
  options?: IOption[];
  onCloseCallback?: (searchInput: string) => void;
  placement?: PopoverPlacement;
  trigger?: string;
  isLazy?: boolean;
  tooltipEnabled?: boolean;
  showSearch?: boolean;
  searchPlaceholder?: string;
  emptyMessage?: string | JSX.Element;
  isMultiSelect?: boolean;
  showSelectedFirst?: boolean;
  isRowReversed?: boolean;
  children: ReactElement;
  closeOnClick?: boolean;
  footer?: ReactNode;
  onSelect?: (e: MouseEvent, option: IOption) => void;
  onLoadOptions?: () => void;
  renderOption?: (option: IOption, index: number) => ReactNode;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setDisabled?: (data: any) => boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  disabledMessage?: ((data: any) => string) | string;
  onActionButtonClicked?: () => void;
  actionButtonText?: string;
  width?: number;
  dropdownId?: string;
  showActivityStatus?: boolean;
  renderDropdownActions?: () => JSX.Element | null;
  loadingOptions?: boolean;
  hideDropdownOption?: boolean;
}

const Dropdown = ({
  options = [],
  placement = PopoverPlacement.Top,
  trigger = 'click',
  tooltipEnabled = true,
  isLazy = false,
  showSearch,
  searchPlaceholder = 'Search...',
  emptyMessage = 'No results found',
  isMultiSelect = false,
  isRowReversed = false,
  showSelectedFirst = true,
  children,
  closeOnClick = true,
  footer,
  onLoadOptions,
  onSelect,
  renderOption,
  setDisabled,
  disabledMessage,
  onActionButtonClicked,
  actionButtonText,
  width,
  dropdownId,
  showActivityStatus,
  loadingOptions = true,
  hideDropdownOption = false,
  renderDropdownActions,
  onCloseCallback,
  ...tippyProps
}: IDropdownProps): JSX.Element => {
  const dropdownContainerRef = useRef<HTMLDivElement | null>(null);
  const dropdownOptionRefs = useRef<HTMLDivElement[] | null[]>([]);
  const nameSearchTextInputRef = useRef<HTMLInputElement | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [tippyInstance, setTippyInstance] = useState<any>();
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  const [nameSearchText, setNameSearchText] = useState('');
  const [searchIsDirty] = useState(false);

  useEffect(() => {
    if (isLazy && options.length && dropdownContainerRef?.current) {
      const { width, height } = dropdownContainerRef?.current.getBoundingClientRect();
      setDimensions({ width, height });
    }
  }, [options, isLazy]);

  const onNameSearchTextChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setNameSearchText(event?.target.value);
  }, []);

  const onCreate = useCallback(
    (instance) => {
      setTippyInstance(instance);
    },
    [setTippyInstance],
  );

  const closeDropdown = useCallback(() => {
    tippyInstance.hide();
  }, [tippyInstance]);

  const [activeListItemIndex, setActiveListItemIndex] = useState(0);

  const focusNextListItem = useCallback(
    (direction) => {
      const isFirstOption = activeListItemIndex === 0;
      const isLastOption = activeListItemIndex === dropdownOptionRefs?.current.length - 1;
      let newIndex = 0;

      if (direction === 'ArrowDown') {
        if (isLastOption) {
          return;
        }

        newIndex = activeListItemIndex + 1;
      } else if (direction === 'ArrowUp') {
        if (isFirstOption) {
          nameSearchTextInputRef?.current?.focus();
          return;
        }

        newIndex = activeListItemIndex - 1;
      }

      const nextListItem = dropdownOptionRefs?.current[newIndex];
      nextListItem?.focus();
      setActiveListItemIndex(newIndex);
    },
    [activeListItemIndex],
  );

  const handleKeyboardNavigation = useCallback(
    (e: KeyboardEvent, elementIsInputField): void => {
      e.persist();

      if (e.key === 'ArrowDown') {
        if (elementIsInputField) {
          e.preventDefault();
          dropdownOptionRefs?.current[0]?.focus();
          setActiveListItemIndex(0);
          return;
        }

        focusNextListItem('ArrowDown');
      }

      if (e.key === 'ArrowUp') {
        if (elementIsInputField) {
          return;
        }

        focusNextListItem('ArrowUp');
      }

      if (e.key === 'Enter') {
        e.preventDefault();

        if (elementIsInputField) {
          return;
        }

        (e.target as HTMLElement).click();
      }

      if (e.key === 'Escape' || e.key === 'Esc') {
        if (typeof onCloseCallback === 'function') {
          onCloseCallback(nameSearchText);
        }

        tippyInstance.hide();
      }
    },
    [focusNextListItem, tippyInstance, nameSearchText, onCloseCallback],
  );

  const onTrigger = useCallback((): void => {
    // Delay focus within the dropdown list to prevent unintended characters being typed in the search input
    setTimeout(() => {
      if (nameSearchTextInputRef.current) {
        nameSearchTextInputRef.current.focus();
      } else {
        dropdownOptionRefs.current[0]?.focus();
        setActiveListItemIndex(0);
      }
    }, 300);
  }, [dropdownOptionRefs, nameSearchTextInputRef]);

  const onShow = useCallback((): void => {
    setTimeout(() => {
      if (typeof onLoadOptions === 'function') {
        onLoadOptions();
      }
      if (dropdownContainerRef?.current) {
        const { width, height } = dropdownContainerRef?.current.getBoundingClientRect();
        setDimensions({ width, height: height < 270 ? 270 : height });
      }
    }, 0);
  }, [dropdownContainerRef, onLoadOptions]);

  const onHidden = useCallback(() => {
    setNameSearchText('');
    closeDropdown();
  }, [setNameSearchText, closeDropdown]);

  const onOptionSelect = useCallback(
    (e: MouseEvent, option: IOption) => {
      if (option.isDisabled || option.isLabel) return;
      onSelect?.(e, option);

      // closeOnClick is overridable at the individual option level.
      if (closeOnClick || option.closeOnClick) {
        closeDropdown();
      }
    },
    [closeOnClick, onSelect, closeDropdown],
  );

  const sortedOptions = useMemo(() => {
    if (!isMultiSelect || !showSelectedFirst) return options;
    return options.sort((a, b) => {
      if (a.isSelected === b.isSelected) return 0;
      if (a.isSelected) return -1;
      if (b.isSelected) return 1;
      return 0;
    });
  }, [options, showSelectedFirst, isMultiSelect]);

  const displayedOptions = useMemo(() => {
    if (!nameSearchText) return sortedOptions;
    return sortedOptions.filter((option: IOption) => option.text?.toLowerCase().includes(nameSearchText.toLowerCase()));
  }, [sortedOptions, nameSearchText]);

  const shouldRenderSearchInput = showSearch === true || (options.length > 5 && showSearch !== false);
  const noOptionsSelected = useMemo(() => {
    return !displayedOptions.filter((option) => option.isSelected).length;
  }, [displayedOptions]);

  const handleOptionClick = useCallback(
    (option: IOption, e: MouseEvent): void => {
      e.stopPropagation();
      // Using a data attribute to disable click when applicable
      const isDisabled = Boolean(e.currentTarget.getAttribute('data-disabled') === 'true');

      if (isDisabled) return;
      setNameSearchText('');

      onOptionSelect(e, option);
    },
    [onOptionSelect],
  );

  const renderOptions = (options: IOption[], isNested = false): ReactNode => {
    return options.map((option: IOption, index: number) => {
      const isDisabled = typeof setDisabled === 'function' ? setDisabled(option.value) : option.isDisabled;
      const disabledMessageValue =
        typeof disabledMessage === 'function' ? disabledMessage(option.value) : disabledMessage;

      if (typeof renderOption === 'function') {
        return renderOption(option, index);
      }

      const tooltipContent = isDisabled ? option.disabledMessage || disabledMessageValue : option.text;
      const tooltipIsDisabled = option.isLabel || !tooltipEnabled ? true : !tooltipContent;

      if (Array.isArray(option.value)) {
        return (
          <OptionGroup key={option.text}>
            <Tooltip content={tooltipContent} delay={isDisabled ? [300, 0] : [500, 0]} disabled={tooltipIsDisabled} />
            <Option
              onClick={partial(handleOptionClick, option)}
              hasChildren={true}
              isSelected={option.isSelected}
              isDisabled={isDisabled}
              isLabel={option.isLabel}
              isBold={option.isBold}
              data-disabled={isDisabled}
              isReverse={isRowReversed}
              data-testid={'dropdown-option-' + index}
              tabIndex={1}
              ref={(el): HTMLDivElement | null => (dropdownOptionRefs.current[index] = el)}
              onKeyDown={(e): void => handleKeyboardNavigation(e, false)}
              id={`dropdown-option${dropdownId ? '-' + dropdownId : ''}`}
            >
              {!!isMultiSelect && (
                <OptionCheck tabIndex={0}>{!!option.isSelected ? <GreenCheckIcon /> : <GrayCircle />}</OptionCheck>
              )}
              <OptionContent>
                <OptionText>{option.text}</OptionText>
                {option?.icon && <OptionIconContainer>{option.icon}</OptionIconContainer>}
              </OptionContent>
            </Option>
            {renderOptions(option.value, true)}
          </OptionGroup>
        );
      }

      return (
        <DropdownOption
          handleOptionClick={handleOptionClick}
          handleKeyboardNavigation={handleKeyboardNavigation}
          showActivityStatus={showActivityStatus}
          key={index}
          option={option}
          isDisabled={isDisabled}
          tooltipContent={tooltipContent}
          tooltipIsDisabled={tooltipIsDisabled}
          isNested={isNested}
          isRowReversed={isRowReversed}
          dropdownOptionRefs={dropdownOptionRefs}
          isMultiSelect={isMultiSelect}
          index={index}
        />
      );
    });
  };

  const renderDropdown = (attrs: {
    'data-placement': Placement;
    'data-reference-hidden'?: string | undefined;
    'data-escaped'?: string | undefined;
  }): ReactNode => {
    if (hideDropdownOption) {
      return <></>;
    }
    return (
      <DropdownContainer
        {...attrs}
        ref={dropdownContainerRef}
        dimensions={nameSearchText ? dimensions : null}
        searchIsDirty={searchIsDirty}
        shouldRenderSearchInput={shouldRenderSearchInput}
        width={width}
      >
        {!!shouldRenderSearchInput && (
          <FixedContainer>
            <SearchTextInput
              ref={nameSearchTextInputRef}
              placeholder={searchPlaceholder}
              value={nameSearchText}
              tabIndex={1}
              onKeyDown={(e): void => handleKeyboardNavigation(e, true)}
              onChange={onNameSearchTextChange}
            />
          </FixedContainer>
        )}
        {renderDropdownActions ? renderDropdownActions() : null}

        {displayedOptions.length ? (
          <>
            <OptionsContainer
              onWheel={(e): void => {
                e.stopPropagation();
              }}
            >
              {renderOptions(displayedOptions)}
            </OptionsContainer>
            {actionButtonText && onActionButtonClicked && (
              <Tooltip content={noOptionsSelected ? 'You must select at least one choice' : actionButtonText}>
                <ActionButtonDiv>
                  <Button
                    isDisabled={noOptionsSelected}
                    onClick={(e: MouseEvent): void => {
                      e.preventDefault();
                      e.stopPropagation();
                      onActionButtonClicked();
                    }}
                  >
                    {actionButtonText}
                  </Button>
                </ActionButtonDiv>
              </Tooltip>
            )}
          </>
        ) : (
          <>
            {loadingOptions && isLazy ? (
              <LoadingIndicator />
            ) : (
              <NoResults>{emptyMessage || 'No results found'}</NoResults>
            )}
          </>
        )}
        {footer && <Footer>{footer}</Footer>}
      </DropdownContainer>
    );
  };

  const allProps = {
    interactive: true,
    appendTo: document.getElementById('root') || undefined,
    placement,
    render: renderDropdown,
    onTrigger,
    onCreate,
    onShow,
    onHidden,
    trigger,
    ...tippyProps,
  };

  if (isLazy) {
    return <LazyTippy {...allProps}>{children}</LazyTippy>;
  }

  return <Tippy {...allProps}>{children}</Tippy>;
};

export default Dropdown;

interface IDropdownContainerProps {
  dimensions: IDimensions | null;
  searchIsDirty?: boolean;
  shouldRenderSearchInput?: boolean;
  width?: number;
}

const DropdownContainer = styled.div`
  min-width: ${({ width }: IDropdownContainerProps): number => (width ? width : 200)}px;
  max-width: ${({ width }: IDropdownContainerProps): number => (width ? width : 200)}px;
  max-height: 270px;
  min-height: ${({ searchIsDirty, shouldRenderSearchInput }: IDropdownContainerProps): string =>
    searchIsDirty && shouldRenderSearchInput ? '260' : '0'}px;
  width: ${({ dimensions }: IDropdownContainerProps): string => (dimensions ? `${dimensions.width}px` : 'auto')};
  height: ${({ dimensions }: IDropdownContainerProps): string => (dimensions ? `${dimensions.height}px` : 'auto')};
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: stretch;

  border: 1px solid #eee;
  border-radius: 5px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  box-shadow: 0 2px 6px 0 rgba(20, 79, 90, 0.23);
  background-color: var(--chakra-colors-background-default);
  > *:first-child {
    padding-top: 5px;
  }
  > *:last-child {
    padding-bottom: 5px;
  }
`;

const FixedContainer = styled.div`
  flex: 0 0 auto;
  padding: 5px;
`;

const SearchTextInput = styled.input`
  width: 100%;
  border-radius: 5px;
  border: 1px solid #ddd;
  padding: 10px;
  margin: 4px 0;
  &:hover {
    border-color: #ccc;
  }
`;

const OptionsContainer = styled.div`
  flex: 1 1 auto;
  overflow-x: hidden;
  overflow-y: auto;
  & + ${FixedContainer} {
    padding-top: 0;
  }
`;

const OptionParent = styled.div`
  background-color: var(--chakra-colors-background-default);
  height: 38px;
  display: flex;
  align-items: center;
  scroll-margin-top: 60px;
`;

const OptionGroup = styled.div`
  &:nth-of-type(n + 2) {
    border-top: 1px solid #e8e9e9;
  }
`;

export const OptionIconContainer = styled.span`
  svg {
    fill: #707070;
    height: 17px;
    position: relative;
    top: 1px;
  }
`;

export const OptionContent = styled.div`
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  overflow: hidden;
`;

interface IOptionProps {
  isNested?: boolean;
  hasChildren?: boolean;
  isSelected?: boolean;
  isDisabled?: boolean;
  isReverse?: boolean;
  isLabel?: boolean;
  isBold?: boolean;
}

export const Option = styled(OptionParent)<IOptionProps>`
  padding: ${({ isNested }): string => (isNested ? '0 16px 0 32px' : '0 16px')};
  cursor: ${({ isLabel }): string => (isLabel ? 'default' : 'pointer')};
  cursor: ${({ isDisabled }): string => (isDisabled ? ' not-allowed' : 'pointer')};
  z-index: 3;
  font-weight: ${({ hasChildren }): string => (hasChildren ? '600' : 'inherit')};
  opacity: ${({ isDisabled }): number => (isDisabled ? 0.6 : 1)};
  /* pointer-events: ${({ isDisabled }): string => (isDisabled ? ' none' : 'auto')}; */
  flex-direction: row;
  &:hover {
    background-color: ${({ isLabel }): string => (isLabel ? '#fff' : '#e6f4f6')};
  }
  &:focus {
    background-color: ${({ isLabel }): string => (isLabel ? '#fff' : '#e6f4f6')};
  }
  ${OptionContent} {
    display: flex;
    align-items: center;
    flex-direction: ${({ isReverse }): string => (isReverse ? 'row-reverse' : 'row')};
    font-weight: ${({ isSelected, isBold }): string => (isSelected || isBold ? '600' : '500')};
  }
  ${OptionIconContainer} {
    margin: ${({ isReverse }): string => (isReverse ? '0 8px 0 0 ' : '0 0 0 8px')};
  }
`;

export const OptionCheck = styled.div`
  flex: 0 0 12px;
  width: 12px;
  height: 12px;
  margin-right: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  & > * {
    width: 100%;
    height: 100%;
  }
`;

export const GrayCircle = styled.div`
  border-radius: 100%;
  background: #c1c9ca;
`;

const OptionText = styled.div`
  width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const NoResults = styled.div`
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Footer = styled.div`
  flex: 0 0 auto;
  border-top: 1px solid #e8e9e9;
  padding: 0;
`;

const ActionButtonDiv = styled.div`
  display: flex;
  justify-content: center;
  padding-top: 12px;
`;
